import { createTheme, alpha } from "@mui/material/styles";
import { esES as coreEsES } from "@mui/material/locale";
import { esES as gridEsES } from "@mui/x-data-grid/locales";
import { esES as pickersEsES } from "@mui/x-date-pickers/locales";
import { es as dateFnsEs } from "date-fns/locale";

const brand = {
  green: "#2E7D46",
  greenBright: "#438879",
  greenDark: "#1F5A31",
  greenSoft: "#E8F3EC",
  // Tonos del logo Alerta Normativa (escudo)
  teal: "#23A37E",
  navy: "#263545",
};

const status = {
  ok: "#2E9E5B",
  warn: "#E4A11B",
  danger: "#D5405E",
};

const brandGradient = `linear-gradient(135deg, ${brand.greenDark} 0%, ${brand.green} 45%, ${brand.teal} 100%)`;

const heroGradient = `linear-gradient(125deg, ${brand.green} 0%, ${brand.green} 48%, ${brand.green} 78%, ${brand.green} 100%)`;

const shape = { borderRadius: 12 };

const bodyFont = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  "Segoe UI",
  "Roboto",
  "Helvetica Neue",
  "Arial",
  "sans-serif",
].join(",");

// Montserrat es la tipografía del logo: se usa en títulos para reforzar la marca
const headingFont = ["Montserrat", bodyFont].join(",");

const typography = {
  fontFamily: bodyFont,
  h1: { fontFamily: headingFont, fontWeight: 800, letterSpacing: "-0.03em" },
  h2: { fontFamily: headingFont, fontWeight: 800, letterSpacing: "-0.025em" },
  h3: { fontFamily: headingFont, fontWeight: 700, letterSpacing: "-0.02em" },
  h4: {
    fontFamily: headingFont,
    fontWeight: 700,
    fontSize: "1.65rem",
    lineHeight: 1.25,
    letterSpacing: "-0.02em",
    "@media (max-width:600px)": { fontSize: "1.35rem" },
  },
  h5: {
    fontFamily: headingFont,
    fontWeight: 700,
    fontSize: "1.3rem",
    lineHeight: 1.3,
    letterSpacing: "-0.015em",
  },
  h6: {
    fontFamily: headingFont,
    fontWeight: 600,
    fontSize: "1.05rem",
    lineHeight: 1.4,
    letterSpacing: "-0.01em",
  },
  subtitle1: { fontWeight: 600 },
  subtitle2: { fontWeight: 600 },
  button: { fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  body2: { lineHeight: 1.55 },
  overline: { fontWeight: 700, letterSpacing: "0.08em" },
};

// Sombras suaves y difusas en lugar de las de Material por defecto
const buildShadows = (isLight) => {
  const c = isLight ? "16, 32, 24" : "0, 0, 0";
  const k = isLight ? 1 : 2.4;
  const s = (a) => Math.min(a * k, 0.7).toFixed(3);
  const levels = [
    "none",
    `0 1px 2px rgba(${c},${s(0.04)}), 0 1px 3px rgba(${c},${s(0.06)})`,
    `0 1px 2px rgba(${c},${s(0.04)}), 0 4px 12px rgba(${c},${s(0.06)})`,
    `0 2px 4px rgba(${c},${s(0.04)}), 0 8px 20px rgba(${c},${s(0.07)})`,
    `0 4px 8px -2px rgba(${c},${s(0.06)}), 0 12px 24px -4px rgba(${c},${s(0.09)})`,
  ];
  const medium = `0 6px 12px -4px rgba(${c},${s(0.08)}), 0 16px 32px -6px rgba(${c},${s(0.12)})`;
  const high = `0 10px 20px -6px rgba(${c},${s(0.1)}), 0 24px 48px -10px rgba(${c},${s(0.16)})`;
  const top = `0 24px 64px -12px rgba(${c},${s(0.28)})`;
  return [
    ...levels,
    ...Array(4).fill(medium), // 5-8 (menús, popovers)
    ...Array(8).fill(high), // 9-16 (drawers)
    ...Array(8).fill(top), // 17-24 (modales)
  ];
};

export const buildTheme = (mode) => {
  const isLight = mode === "light";

  const palette = {
    mode,
    primary: {
      main: brand.green,
      light: brand.greenBright,
      dark: brand.greenDark,
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: isLight ? brand.navy : "#94A3B8",
      contrastText: "#FFFFFF",
    },
    success: { main: status.ok },
    warning: { main: status.warn },
    error: { main: status.danger },
    info: { main: "#3B82C4" },
    background: {
      default: isLight ? "#F4F7F5" : "#0F1413",
      paper: isLight ? "#FFFFFF" : "#171D1C",
    },
    text: {
      primary: isLight ? "#1A2420" : "#E8EAEA",
      secondary: isLight ? "#5B6B63" : "#9BA3A2",
    },
    divider: isLight ? "#E4EAE6" : "#283030",
    action: {
      hover: isLight ? alpha(brand.green, 0.05) : alpha(brand.greenBright, 0.1),
      selected: isLight ? brand.greenSoft : alpha(brand.greenBright, 0.16),
    },
  };

  const shadows = buildShadows(isLight);
  const inputBorder = isLight ? "#D8E0DB" : "#303837";
  const focusRing = (color) => `0 0 0 3px ${alpha(color, isLight ? 0.16 : 0.28)}`;

  // Colores de chip "tonales": fondo suave + texto en el mismo tono
  const tonal = (color) => ({
    backgroundColor: alpha(color.main, isLight ? 0.12 : 0.2),
    color: isLight ? color.dark : color.light,
  });

  const headBackground = isLight ? "#F7F9F8" : "#1C2322";

  return createTheme({
    palette,
    shape,
    typography,
    shadows,
    // Fondo del encabezado de DataGrid (API oficial de MUI X v7)
    mixins: { MuiDataGrid: { containerBackground: headBackground } },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: `${palette.divider} transparent`,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
          },
          // Avisos globales (notistack) bajo el AppBar
          ".notistack-SnackbarContainer.an-toast-top": { top: 76 },
          kbd: {
            fontFamily: bodyFont,
            fontSize: "0.7rem",
            fontWeight: 600,
            lineHeight: 1,
            padding: "3px 6px",
            borderRadius: 6,
            color: palette.text.secondary,
            backgroundColor: isLight ? "#F1F4F2" : "#232A29",
            border: `1px solid ${palette.divider}`,
            borderBottomWidth: 2,
          },
          "::selection": { backgroundColor: alpha(brand.green, 0.2) },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isLight ? "#D3DBD6" : "#323A39",
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: isLight ? "#B9C4BD" : "#46504E",
          },
          "@keyframes anFadeUp": {
            from: { opacity: 0, transform: "translateY(6px)" },
            to: { opacity: 1, transform: "none" },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          rounded: { borderRadius: 14 },
          elevation: { border: `1px solid ${palette.divider}` },
          outlined: { borderColor: palette.divider },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            transition: "box-shadow .2s, border-color .2s",
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: alpha(palette.background.paper, isLight ? 0.82 : 0.78),
            backdropFilter: "saturate(180%) blur(14px)",
            color: palette.text.primary,
            border: "none",
            borderBottom: `1px solid ${palette.divider}`,
            borderRadius: 0,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.background.paper,
            border: "none",
            borderRight: `1px solid ${palette.divider}`,
            borderRadius: 0,
            backgroundImage: "none",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            paddingInline: 16,
            transition:
              "background-color .15s, box-shadow .15s, border-color .15s, color .15s",
          },
          sizeLarge: { paddingBlock: 10, fontSize: "0.95rem" },
          containedPrimary: {
            boxShadow: `0 1px 2px ${alpha(brand.greenDark, 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.12)}`,
            "&:hover": {
              backgroundColor: brand.greenDark,
              boxShadow: `0 4px 14px ${alpha(brand.green, 0.3)}`,
            },
          },
          outlinedPrimary: {
            borderColor: alpha(brand.green, 0.4),
            "&:hover": { borderColor: brand.green },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: "background-color .15s, color .15s",
          },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiInputLabel: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isLight ? "#FFFFFF" : alpha("#000", 0.18),
            transition: "box-shadow .15s, background-color .15s",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: inputBorder,
              transition: "border-color .15s",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(brand.green, 0.45),
            },
            "&.Mui-focused": { boxShadow: focusRing(brand.green) },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: brand.green,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: status.danger,
            },
            "&.Mui-error.Mui-focused": { boxShadow: focusRing(status.danger) },
            "&.Mui-disabled": {
              backgroundColor: isLight ? "#F4F6F5" : alpha("#000", 0.1),
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.divider,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const color =
              ownerState.color && ownerState.color !== "default"
                ? theme.palette[ownerState.color]
                : null;
            return {
              borderRadius: 8,
              fontWeight: 600,
              ...(color &&
                ownerState.variant !== "outlined" && {
                  ...tonal(color),
                  "& .MuiChip-icon": { color: "inherit" },
                  "& .MuiChip-deleteIcon": {
                    color: alpha(isLight ? color.dark : color.light, 0.6),
                    "&:hover": { color: isLight ? color.dark : color.light },
                  },
                  "&.MuiChip-clickable:hover": {
                    backgroundColor: alpha(color.main, isLight ? 0.2 : 0.3),
                  },
                }),
            };
          },
          sizeSmall: { height: 24, fontSize: "0.75rem" },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: ({ ownerState, theme }) => {
            const key = ownerState.color || ownerState.severity || "success";
            const color = theme.palette[key] || theme.palette.info;
            return {
              borderRadius: 12,
              ...(ownerState.variant === "standard" && {
                border: `1px solid ${alpha(color.main, 0.28)}`,
              }),
              ...(ownerState.variant !== "standard" && ownerState.variant !== "outlined" && {
                border: "none",
              }),
            };
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { borderRadius: 14 } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${palette.divider}` },
          head: {
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: palette.text.secondary,
            backgroundColor: isLight ? "#F7F9F8" : "#1C2322",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-of-type td": { borderBottom: "none" },
            transition: "background-color .12s",
            ".MuiTableBody-root &:hover": {
              backgroundColor: palette.action.hover,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          selectLabel: { fontSize: "0.8rem", color: palette.text.secondary },
          displayedRows: { fontSize: "0.8rem", color: palette.text.secondary },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 3, borderRadius: "3px 3px 0 0" },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            minHeight: 46,
          },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: {
            backgroundColor: isLight ? brand.navy : "#2A3B3A",
            fontSize: "0.72rem",
            fontWeight: 500,
            borderRadius: 8,
            padding: "6px 10px",
            boxShadow: shadows[4],
          },
          arrow: { color: isLight ? brand.navy : "#2A3B3A" },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 12 },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: "0.875rem",
            minHeight: 38,
            "&.Mui-selected": { backgroundColor: palette.action.selected },
          },
        },
      },
      MuiPopover: {
        styleOverrides: { paper: { borderRadius: 12 } },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: { borderRadius: 12, marginTop: 4 },
          option: { borderRadius: 8, marginInline: 6, fontSize: "0.875rem" },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 18 },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: headingFont,
            fontWeight: 700,
            fontSize: "1.15rem",
            letterSpacing: "-0.01em",
            padding: "20px 24px 12px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: "12px 24px 20px" } },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            "&:not(.MuiBackdrop-invisible)": {
              backgroundColor: alpha(isLight ? "#0E1A14" : "#000", isLight ? 0.42 : 0.6),
              backdropFilter: "blur(3px)",
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 999 } },
      },
      MuiLink: {
        defaultProps: { underline: "hover" },
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiAvatar: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginInline: 8,
            "&.Mui-selected": {
              backgroundColor: palette.action.selected,
              "&:hover": { backgroundColor: palette.action.selected },
            },
          },
        },
      },
      // Avisos locales de cada vista: misma posición que los globales
      MuiSnackbar: {
        defaultProps: { anchorOrigin: { vertical: "top", horizontal: "right" } },
        styleOverrides: {
          root: ({ ownerState, theme }) => ({
            "& .MuiAlert-root": { boxShadow: shadows[6] },
            ...(ownerState.anchorOrigin?.vertical === "top" && {
              top: 70,
              [theme.breakpoints.up("sm")]: { top: 80 },
            }),
          }),
        },
      },
      // Los 13 selectores de fecha heredan el idioma español desde aquí
      MuiLocalizationProvider: {
        defaultProps: { adapterLocale: dateFnsEs },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          separator: { marginInline: 4, color: palette.text.disabled },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: isLight ? "#D8E0DB" : "#303837",
            "&.Mui-active": { color: brand.green },
            "&.Mui-completed": { color: brand.green },
            "&.Mui-error": { color: status.danger },
          },
          text: { fontWeight: 700, fontFamily: bodyFont },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          disableRowSelectionOnClick: true,
          pageSizeOptions: [10, 25, 50, 100],
        },
        styleOverrides: {
          root: {
            borderColor: palette.divider,
            borderRadius: 14,
            backgroundColor: palette.background.paper,
            fontSize: "0.875rem",
            "--DataGrid-rowBorderColor": palette.divider,
            "& .MuiDataGrid-row:hover": { backgroundColor: palette.action.hover },
          },
          columnHeader: {
            "&:focus, &:focus-within": { outline: "none" },
          },
          columnHeaderTitle: {
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: palette.text.secondary,
          },
          cell: {
            "&:focus, &:focus-within": { outline: "none" },
          },
          columnSeparator: { color: palette.divider },
          footerContainer: { borderTopColor: palette.divider },
          toolbarContainer: {
            padding: "10px 12px",
            gap: 8,
            borderBottom: `1px solid ${palette.divider}`,
          },
          overlay: { backgroundColor: alpha(palette.background.paper, 0.4) },
          panelWrapper: { borderRadius: 12 },
        },
      },
    },
  },
  pickersEsES,
  gridEsES,
  coreEsES);
};

export { brand, status, brandGradient, heroGradient, headingFont };
